import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import PublicLayout from "@/components/layout/PublicLayout";
import DashboardLayout from "@/components/layout/DashboardLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Standards from "@/pages/Standards";
import Registry from "@/pages/Registry";
import Verify from "@/pages/Verify";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";

import DashboardHome from "@/pages/dashboard/DashboardHome";
import Applications from "@/pages/dashboard/Applications";
import ApplicationNew from "@/pages/dashboard/ApplicationNew";
import ApplicationDetail from "@/pages/dashboard/ApplicationDetail";
import Credentials from "@/pages/dashboard/Credentials";
import Documents from "@/pages/dashboard/Documents";
import AdminUsers from "@/pages/dashboard/AdminUsers";
import AdminPayments from "@/pages/dashboard/AdminPayments";
import AdminAudit from "@/pages/dashboard/AdminAudit";
import AdminEmails from "@/pages/dashboard/AdminEmails";
import AdminSeals from "@/pages/dashboard/AdminSeals";
import AdminTrash from "@/pages/dashboard/AdminTrash";
import Drafts from "@/pages/dashboard/Drafts";
import Certificate from "@/pages/Certificate";

import PublicSchools from "@/pages/PublicSchools";
import SchoolApplications from "@/pages/dashboard/SchoolApplications";
import SchoolApplicationNew from "@/pages/dashboard/SchoolApplicationNew";
import SchoolApplicationDetail from "@/pages/dashboard/SchoolApplicationDetail";
import SiteVisits from "@/pages/dashboard/SiteVisits";
import SchoolsAdmin from "@/pages/dashboard/SchoolsAdmin";
import Renewals from "@/pages/dashboard/Renewals";
import CERecords from "@/pages/dashboard/CERecords";
import Disciplinary from "@/pages/dashboard/Disciplinary";
import Reinstatement from "@/pages/dashboard/Reinstatement";

const withPublic = (el) => <PublicLayout>{el}</PublicLayout>;
const withDashboard = (el, roles) => (
  <ProtectedRoute roles={roles}>
    <DashboardLayout>{el}</DashboardLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" closeButton />
          <Routes>
            {/* Public */}
            <Route path="/" element={withPublic(<Home />)} />
            <Route path="/about" element={withPublic(<About />)} />
            <Route path="/standards" element={withPublic(<Standards />)} />
            <Route path="/registry" element={withPublic(<Registry />)} />
            <Route path="/schools" element={withPublic(<PublicSchools />)} />
            <Route path="/verify" element={withPublic(<Verify />)} />
            <Route path="/verify/:credentialNumber" element={withPublic(<Verify />)} />
            <Route path="/certificate/:kind/:identifier" element={<ProtectedRoute><Certificate /></ProtectedRoute>} />
            <Route path="/contact" element={withPublic(<Contact />)} />
            <Route path="/login" element={withPublic(<Login />)} />
            <Route path="/register" element={withPublic(<Register />)} />
            <Route path="/forgot-password" element={withPublic(<ForgotPassword />)} />

            {/* Authenticated */}
            <Route path="/dashboard" element={withDashboard(<DashboardHome />)} />
            <Route path="/dashboard/applications" element={withDashboard(<Applications />)} />
            <Route path="/dashboard/applications/new" element={withDashboard(<ApplicationNew />, ["applicant", "practitioner"])} />
            <Route path="/dashboard/applications/:id" element={withDashboard(<ApplicationDetail />)} />
            <Route path="/dashboard/credentials" element={withDashboard(<Credentials />)} />
            <Route path="/dashboard/documents" element={withDashboard(<Documents />)} />
            <Route path="/dashboard/users" element={withDashboard(<AdminUsers />, ["admin"])} />
            <Route path="/dashboard/payments" element={withDashboard(<AdminPayments />, ["admin"])} />
            <Route path="/dashboard/audit" element={withDashboard(<AdminAudit />, ["admin"])} />
            <Route path="/dashboard/emails" element={withDashboard(<AdminEmails />, ["admin"])} />
            <Route path="/dashboard/seals" element={withDashboard(<AdminSeals />, ["admin"])} />
            <Route path="/dashboard/admin/trash" element={withDashboard(<AdminTrash />, ["admin"])} />
            <Route path="/dashboard/drafts" element={withDashboard(<Drafts />, ["applicant", "practitioner"])} />

            {/* ACIS division */}
            <Route path="/dashboard/schools" element={withDashboard(<SchoolApplications />)} />
            <Route path="/dashboard/schools/new" element={withDashboard(<SchoolApplicationNew />)} />
            <Route path="/dashboard/schools/:id" element={withDashboard(<SchoolApplicationDetail />)} />
            <Route path="/dashboard/site-visits" element={withDashboard(<SiteVisits />)} />
            <Route path="/dashboard/accredited-schools" element={withDashboard(<SchoolsAdmin />, ["admin", "reviewer"])} />

            {/* IBTHM extended */}
            <Route path="/dashboard/renewals" element={withDashboard(<Renewals />)} />
            <Route path="/dashboard/ce" element={withDashboard(<CERecords />)} />
            <Route path="/dashboard/disciplinary" element={withDashboard(<Disciplinary />, ["admin", "reviewer"])} />
            <Route path="/dashboard/reinstatement" element={withDashboard(<Reinstatement />)} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
